import z from "zod";
import {
  CashboxIdScheme,
  DeviceConfigIdScheme,
  LabelFormatIdScheme,
  LayoutIdScheme,
  PaymentMethodIdScheme,
  PrinterIdScheme,
  StockLocationIdScheme,
} from "./ids";
import { ProductStyle } from "./enums/ProductStyle";
import { TipPromptBehavior } from "./enums/TipPromptBehavior";
import { PrepTicketPromptMode } from "./enums/PrepTicketPromptMode";
import { PaymentMethodButtonLayoutScheme } from "./paymentMethodButtonLayout";
import { CustomerDisplaySettingsScheme } from "./customerDisplaySettings";
import { PaymentCompleteAutoCloseSecondsScheme } from "./store";

export const DeviceConfigDBScheme = z.object({
  id: DeviceConfigIdScheme,
  name: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),

  // settings
  layoutId: LayoutIdScheme.nullable().optional(),
  defaultReceiptPrinter: z.string().nullable().optional(),
  showReceiptQr: z.boolean().nullable().optional(),
  paymentCompleteAutoCloseSeconds:
    PaymentCompleteAutoCloseSecondsScheme.nullable().optional(),

  enablePriceInDecimals: z.boolean().nullable().optional(),
  enableOpenPriceInDecimals: z.boolean().nullable().optional(),

  enableBarcodeScanner: z.boolean().nullable().optional(),
  enableCameraScanning: z.boolean().nullable().optional(),
  numberOfColumnsInStore: z.number().nullable().optional(),
  productStyle: z.enum(ProductStyle).nullable().optional(),
  paymentMethodsOrder: z.array(PaymentMethodIdScheme).nullable().optional(),
  paymentMethodButtonLayout:
    PaymentMethodButtonLayoutScheme.nullable().optional(),
  customerDisplayActive: z.boolean().nullable().optional(),
  customerDisplayImage: z.string().nullable().optional(),
  customerDisplayScaling: z.number().nullable().optional(),
  customerDisplaySettings: CustomerDisplaySettingsScheme.nullable().optional(),
  tipPromptBehavior: z.enum(TipPromptBehavior).nullable().optional(),
  prepTicketPromptMode: z.enum(PrepTicketPromptMode).nullable().optional(),
  serviceOrderPrinterId: z.string().nullable().optional(),
  productSyncIntervalMinutes: z.number().nullable().optional(),
  productFullSyncIntervalMinutes: z.number().nullable().optional(),
  fullProductSyncOnStart: z.boolean().nullable().optional(),

  // label printing
  defaultLabelFormatId: LabelFormatIdScheme.nullable().optional(),
  defaultLabelPrinterId: PrinterIdScheme.nullable().optional(),

  fullscreenFloorPlan: z.boolean().nullable().optional(),
  disableFloorPlan: z.boolean().nullable().optional(),
  requireStaff: z.boolean().nullable().optional(),
  hideChangeUserButton: z.boolean().nullable().optional(),
  cashboxId: CashboxIdScheme.nullable().optional(),
  stockLocationId: StockLocationIdScheme.nullable().optional(),
});

export const RestGetDeviceConfig = DeviceConfigDBScheme;
export const RestCreateDeviceConfig = DeviceConfigDBScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
  .partial()
  .required({ name: true });
export const RestUpdateDeviceConfig = DeviceConfigDBScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export type DBDeviceConfig = z.infer<typeof DeviceConfigDBScheme>;
export type RestDeviceConfig = z.infer<typeof RestGetDeviceConfig>;
export type RestCreateDeviceConfig = z.infer<typeof RestCreateDeviceConfig>;
export type RestUpdateDeviceConfig = z.infer<typeof RestUpdateDeviceConfig>;
